import React, { useState, useRef, useEffect } from 'react';
import { useTranslations } from 'use-intl';
import { Mic, Square, PlayCircle, PauseCircle, RotateCcw } from 'react-feather';
import Modal from '@/UIlibrary/Modal/Modal';
import Button from '@/UIlibrary/Button/Button';
import IconButton from '@/UIlibrary/IconButton/IconButton';
import { Typography } from '@/UIlibrary/Typography/Typography';
import lamejs from 'lamejs';

interface VoiceRecordModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: (audioBlob: Blob) => Promise<void>;
}

const VoiceRecordModal: React.FC<VoiceRecordModalProps> = ({ isOpen, onClose, onSave }) => {
	const t = useTranslations('Support');
	const [isRecording, setIsRecording] = useState(false);
	const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [recordingTime, setRecordingTime] = useState(0);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [isProcessing, setIsProcessing] = useState(false);

	const mediaRecorderRef = useRef<MediaRecorder | null>(null);
	const mediaStreamRef = useRef<MediaStream | null>(null);
	const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
	const timerRef = useRef<number | null>(null);
	const progressIntervalRef = useRef<number | null>(null);
	const chunksRef = useRef<Blob[]>([]);

	const resetState = () => {
		setIsRecording(false);
		setAudioBlob(null);
		setIsPlaying(false);
		setRecordingTime(0);
		setCurrentTime(0);
		setDuration(0);
		setIsProcessing(false);
		chunksRef.current = [];

		if (audioPlayerRef.current) {
			audioPlayerRef.current.pause();
			audioPlayerRef.current = null;
		}
		if (timerRef.current) {
			clearInterval(timerRef.current);
		}
		if (progressIntervalRef.current) {
			clearInterval(progressIntervalRef.current);
		}
		if (mediaStreamRef.current) {
			mediaStreamRef.current.getTracks().forEach((track) => track.stop());
		}
		if (mediaRecorderRef.current) {
			mediaRecorderRef.current = null;
		}
	};

	useEffect(() => {
		if (!isOpen) {
			resetState();
		}
	}, [isOpen]);

	useEffect(() => {
		return () => {
			resetState();
		};
	}, []);

	const convertToMp3 = async (audioData: Float32Array, sampleRate: number): Promise<Blob> => {
		const mp3Encoder = new lamejs.Mp3Encoder(1, sampleRate, 128);
		const sampleBlockSize = 1152;
		const mp3Data = [];

		// Convert Float32Array to Int16Array
		const samples = new Int16Array(audioData.length);
		for (let i = 0; i < audioData.length; i++) {
			samples[i] = audioData[i] < 0 ? audioData[i] * 0x8000 : audioData[i] * 0x7fff;
		}

		// Encode to MP3
		for (let i = 0; i < samples.length; i += sampleBlockSize) {
			const sampleChunk = samples.subarray(i, i + sampleBlockSize);
			const mp3buf = mp3Encoder.encodeBuffer(sampleChunk);
			if (mp3buf.length > 0) {
				mp3Data.push(mp3buf);
			}
		}

		// Get the last chunk of MP3 data
		const mp3buf = mp3Encoder.flush();
		if (mp3buf.length > 0) {
			mp3Data.push(mp3buf);
		}

		// Combine all MP3 data chunks into a single Blob
		const blob = new Blob(mp3Data, { type: 'audio/mp3' });
		return blob;
	};

	const webmToAudioBuffer = async (webmBlob: Blob): Promise<AudioBuffer> => {
		const audioContext = new AudioContext();
		const arrayBuffer = await webmBlob.arrayBuffer();
		return await audioContext.decodeAudioData(arrayBuffer);
	};

	const startRecording = async () => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			mediaStreamRef.current = stream;

			mediaRecorderRef.current = new MediaRecorder(stream, {
				mimeType: 'audio/webm;codecs=opus'
			});

			mediaRecorderRef.current.ondataavailable = (event) => {
				if (event.data.size > 0) {
					chunksRef.current.push(event.data);
				}
			};

			mediaRecorderRef.current.onstop = async () => {
				setIsProcessing(true);
				try {
					const webmBlob = new Blob(chunksRef.current, { type: 'audio/webm;codecs=opus' });
					const audioBuffer = await webmToAudioBuffer(webmBlob);
					const mp3Blob = await convertToMp3(audioBuffer.getChannelData(0), audioBuffer.sampleRate);
					setAudioBlob(mp3Blob);
				} catch (error) {
					console.error('Error converting to MP3:', error);
				} finally {
					setIsProcessing(false);
					chunksRef.current = [];
				}
			};

			mediaRecorderRef.current.start();
			setIsRecording(true);
			setRecordingTime(0);

			timerRef.current = window.setInterval(() => {
				setRecordingTime((prev) => prev + 1);
			}, 1000);
		} catch (error) {
			console.error('Error accessing microphone:', error);
		}
	};

	const stopRecording = async () => {
		if (!mediaRecorderRef.current || !isRecording) return;

		setIsRecording(false);
		if (timerRef.current) {
			clearInterval(timerRef.current);
		}

		mediaRecorderRef.current.stop();

		if (mediaStreamRef.current) {
			mediaStreamRef.current.getTracks().forEach((track) => track.stop());
		}
	};

	const startProgressUpdate = () => {
		if (progressIntervalRef.current) {
			clearInterval(progressIntervalRef.current);
		}
		progressIntervalRef.current = window.setInterval(() => {
			if (audioPlayerRef.current) {
				setCurrentTime(audioPlayerRef.current.currentTime);
			}
		}, 100);
	};

	const stopProgressUpdate = () => {
		if (progressIntervalRef.current) {
			clearInterval(progressIntervalRef.current);
		}
	};

	const togglePlayback = () => {
		if (!audioBlob) return;

		if (!audioPlayerRef.current) {
			const audioUrl = URL.createObjectURL(audioBlob);
			audioPlayerRef.current = new Audio(audioUrl);

			audioPlayerRef.current.onloadedmetadata = () => {
				setDuration(audioPlayerRef.current!.duration);
			};

			audioPlayerRef.current.onended = () => {
				setIsPlaying(false);
				stopProgressUpdate();
				setCurrentTime(0);
			};
		}

		if (isPlaying) {
			audioPlayerRef.current.pause();
			stopProgressUpdate();
		} else {
			audioPlayerRef.current.play();
			startProgressUpdate();
		}
		setIsPlaying(!isPlaying);
	};

	const formatTime = (seconds: number) => {
		const mins = Math.floor(seconds / 60);
		const secs = Math.floor(seconds % 60);
		return `${mins}:${secs.toString().padStart(2, '0')}`;
	};

	const handleSave = async () => {
		setIsProcessing(true);
		if (audioBlob) {
			await onSave(audioBlob);
			resetState();
			onClose();
		}
		setIsProcessing(false);
	};

	const handleCancel = () => {
		resetState();
		onClose();
	};

	if (!isOpen) return null;

	return (
		<Modal isOpen={isOpen} onClose={handleCancel} closeable>
			<div className="bg-white rounded-lg p-2 max-w-md w-full">
				<Typography size="lg">{t('RecordVoiceNote')}</Typography>

				<div className="flex flex-col items-center gap-6">
					<div className="text-xl font-mono mt-6">
						{isRecording ? formatTime(recordingTime) : formatTime(currentTime)}
					</div>

					{audioBlob && !isRecording && (
						<div className="w-full space-y-2">
							<div className="w-full bg-gray-200 rounded-full h-2 cursor-pointer">
								<div
									className="bg-blue-500 h-2 rounded-full transition-all duration-100"
									style={{
										width: `${(currentTime / duration) * 100}%`
									}}
								/>
							</div>
							<div className="flex justify-between text-xs text-gray-500">
								<span>{formatTime(currentTime)}</span>
							</div>
						</div>
					)}

					<div className="flex space-x-4">
						{!audioBlob ? (
							<IconButton
								color={isRecording ? 'error' : 'primary'}
								onClick={isRecording ? stopRecording : startRecording}
								icon={isRecording ? <Square className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
								disabled={isProcessing}
							/>
						) : (
							<>
								<IconButton
									color="transparent"
									onClick={resetState}
									icon={<RotateCcw className="w-5 h-5" />}
								/>
								<Button
									color="primary"
									onClick={handleSave}
									label={t('Send')}
									disabled={isProcessing}
								/>
								<IconButton
									color="secondary"
									onClick={togglePlayback}
									icon={
										isPlaying ? (
											<PauseCircle className="w-5 h-5" />
										) : (
											<PlayCircle className="w-5 h-5" />
										)
									}
								/>
							</>
						)}
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default VoiceRecordModal;
